import { ParticipantLabel } from '@seen/core/models/participantLabel'
import { Button } from '@seen/ui'
import { IconDots, IconPlus } from '@tabler/icons-react'
import type { ColumnDef } from '@tanstack/react-table'
import { sortBy } from 'lodash-es'
import { type JSX, useMemo, useState } from 'react'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogButton } from '#src/components/core/dialog/DialogButton.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { DropdownMenu } from '#src/components/core/dropdown-menu/DropdownMenu.tsx'
import { DropdownMenuContent } from '#src/components/core/dropdown-menu/DropdownMenuContent.tsx'
import { DropdownMenuDeleteItem } from '#src/components/core/dropdown-menu/DropdownMenuDeleteItem.tsx'
import { DropdownMenuTrigger } from '#src/components/core/dropdown-menu/DropdownMenuTrigger.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { PageLayout } from '#src/components/display/PageLayout.tsx'
import { PractitionerDisplay } from '#src/components/display/PractitionerDisplay.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { LabelDialogContent } from '#src/components/LabelDialogContent.tsx'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export function ParticipantLabelsPage(): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const { data: unsortedParticipantLabels } = useSearch(
    ParticipantLabel,
    ParticipantLabel.baseSearchQuery()
  )
  const participantLabels = useMemo(
    () => sortBy(unsortedParticipantLabels, 'name'),
    [unsortedParticipantLabels]
  )

  const patch = usePatchMutation<ParticipantLabel>().mutate
  const [composerOpen, setComposerOpen] = useState(false)
  const [selectedLabel, setSelectedLabel] = useState<string | undefined>(
    undefined
  )

  const columns: ColumnDef<ParticipantLabel>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            <div className="flex size-3.5 shrink-0 items-center justify-center">
              <div
                style={{ backgroundColor: row.original.color }}
                className="size-2.25 rounded-full"
              />
            </div>
            {row.original.name}
          </div>
        ),
      },
      {
        accessorKey: 'created',
        header: 'Created',
        cell: ({ row }) =>
          row.original.created ? (
            <HoverDate date={row.original.created} />
          ) : null,
      },
      {
        accessorKey: 'lastUpdated',
        header: 'Updated',
        cell: ({ row }) =>
          row.original.lastUpdated ? (
            <HoverDate date={row.original.lastUpdated} />
          ) : null,
      },
      {
        accessorKey: 'lastUpdatedBy',
        header: 'Updated by',
        cell: ({ row }) => {
          const author = row.original.lastUpdatedByPractitioner
          return author ? <PractitionerDisplay practitioner={author} /> : null
        },
      },
      {
        id: 'actions',
        cell: ({ row }) => {
          const participantLabel = row.original
          return (
            <div className="flex items-center justify-end gap-1">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="-my-3">
                    <IconDots className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenuDeleteItem
                    deleteConfirmationProps={{
                      title: `Delete "${participantLabel.name}"?`,
                      description:
                        'Deleted labels can still be recovered by contacting our team.',
                      onConfirm: () => {
                        patch({
                          instance: participantLabel,
                          mutateFn: (pl) => {
                            pl.delete()
                          },
                        })
                      },
                    }}
                  />
                  <DropdownMenuMedplumLinkItem to={participantLabel} />
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )
        },
      },
    ],
    [patch]
  )

  if (!participantLabels) {
    return <Loader />
  }

  return (
    <PageLayout
      title="Participant Labels"
      actions={
        !isReadOnly ? (
          <Dialog
            open={composerOpen}
            onOpenChange={(open) => {
              setComposerOpen(open)
              setSelectedLabel(undefined)
            }}
          >
            <DialogButton
              type="button"
              variant="outline"
              className="flex items-center gap-2"
            >
              <IconPlus className="size-4" />
              New label…
            </DialogButton>
            <DialogDisplay>
              <LabelDialogContent
                onSubmit={() => {
                  setComposerOpen(false)
                  setSelectedLabel(undefined)
                }}
                label={selectedLabel}
                labelClass={ParticipantLabel}
              />
            </DialogDisplay>
          </Dialog>
        ) : undefined
      }
    >
      <DataTable
        columns={columns}
        data={participantLabels}
        onRowClick={(row) => {
          setSelectedLabel(row.reference.reference)
          setComposerOpen(true)
        }}
        deleteRowProps={(participantLabel) => ({
          title: `Delete "${participantLabel.name}"?`,
          description:
            'Deleted labels can still be recovered by contacting our team.',
          onConfirm: () => {
            patch({
              instance: participantLabel,
              mutateFn: (pl) => {
                pl.delete()
              },
            })
          },
        })}
      />
    </PageLayout>
  )
}
